import React, { useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { PLANO_GOVERNO } from "../data/planoGoverno";

const Icon = ({ name, ...p }) => {
  const C = Icons[name] || Icons.Sparkles;
  return <C {...p} />;
};

export default function PlanoGoverno() {
  const [openEixo, setOpenEixo] = useState(PLANO_GOVERNO.eixos[0].id);

  return (
    <div data-testid="page-plano-governo" className="pt-24 md:pt-32 bg-[#FAF9F6]">
      {/* Header */}
      <header className="max-w-7xl mx-auto px-6 md:px-10 pb-16 md:pb-20">
        <div className="text-[11px] uppercase tracking-[0.28em] text-[#003F88] font-semibold">
          <span className="accent-line" />
          Plano de Governo · Proposta apresentada em {PLANO_GOVERNO.ano_proposta}
        </div>
        <h1 className="font-serif-display text-5xl md:text-7xl leading-[0.95] tracking-tight mt-6">
          {PLANO_GOVERNO.lema.split(" · ").map((part, i) => (
            <span key={i}>
              {i === 1 ? <em className="italic font-light">{part}</em> : part}
              {i === 0 && <br />}
            </span>
          ))}
        </h1>
        <p className="mt-6 text-base md:text-lg uppercase tracking-widest text-neutral-500">
          {PLANO_GOVERNO.periodo}
        </p>
      </header>

      {/* Introdução */}
      <section className="bg-white border-y border-neutral-200">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-20 md:py-24">
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#003F88] font-semibold mb-6">
            <span className="accent-line" /> Apresentação
          </div>
          {PLANO_GOVERNO.introducao.split("\n\n").map((p, i) => (
            <p
              key={i}
              className={`font-body text-lg md:text-xl leading-relaxed text-neutral-700 font-light ${i > 0 ? "mt-6" : ""} ${i === 0 ? "drop-cap" : ""}`}
            >
              {p}
            </p>
          ))}
        </div>
      </section>

      {/* Conquistas já articuladas */}
      <section className="bg-[#003F88] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#FFC72C] font-semibold">
              <span className="inline-block w-14 h-[3px] bg-[#FFC72C] align-middle mr-4" />
              Já em curso
            </div>
            <h2 className="font-serif-display text-3xl md:text-5xl leading-tight mt-6">
              Conquistas <em className="italic font-light">já articuladas</em> com o Governo Federal
            </h2>
          </div>
          <ul className="lg:col-span-8 grid md:grid-cols-2 gap-4">
            {PLANO_GOVERNO.conquistas_ja_articuladas.map((c, i) => (
              <li
                key={i}
                className="bg-white/5 backdrop-blur-sm border border-white/10 p-5 text-sm md:text-base"
                data-testid={`conquista-${i}`}
              >
                <span className="text-[#FFC72C] font-serif-display text-2xl mr-3">{String(i + 1).padStart(2, "0")}</span>
                {c}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Eixos */}
      <section className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
            <div className="lg:col-span-7">
              <div className="text-[11px] uppercase tracking-[0.28em] text-[#003F88] font-semibold">
                <span className="accent-line" />
                Os 7 eixos do plano
              </div>
              <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight mt-6">
                160+ propostas, <em className="italic font-light">uma cidade inteira</em>.
              </h2>
            </div>
            <p className="lg:col-span-5 text-base md:text-lg text-neutral-600 font-light leading-relaxed">
              Clique em cada eixo para expandir o diagnóstico e a íntegra das propostas apresentadas.
            </p>
          </div>

          <div className="space-y-3">
            {PLANO_GOVERNO.eixos.map((eixo) => {
              const open = openEixo === eixo.id;
              return (
                <div
                  key={eixo.id}
                  id={eixo.id}
                  data-testid={`eixo-${eixo.id}`}
                  className={`border bg-white transition-colors ${open ? "border-[#003F88]" : "border-neutral-200"}`}
                >
                  <button
                    onClick={() => setOpenEixo(open ? null : eixo.id)}
                    data-testid={`eixo-toggle-${eixo.id}`}
                    className="w-full px-6 md:px-10 py-6 md:py-8 flex items-center justify-between gap-6 text-left"
                  >
                    <div className="flex items-center gap-5 md:gap-7">
                      <span className={`inline-flex items-center justify-center w-12 h-12 md:w-14 md:h-14 shrink-0 ${open ? "bg-[#003F88] text-[#FFC72C]" : "bg-[#FAF9F6] text-[#003F88]"}`}>
                        <Icon name={eixo.icon} size={24} strokeWidth={1.5} />
                      </span>
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 font-semibold">
                          Eixo
                        </div>
                        <h3 className="font-serif-display text-2xl md:text-3xl lg:text-4xl leading-tight text-neutral-900">
                          {eixo.nome}
                        </h3>
                      </div>
                    </div>
                    <Icons.ChevronDown
                      size={28}
                      className={`shrink-0 text-[#003F88] transition-transform ${open ? "rotate-180" : ""}`}
                    />
                  </button>

                  {open && (
                    <div className="px-6 md:px-10 pb-10 md:pb-12 border-t border-neutral-200">
                      <p className="mt-8 max-w-4xl text-base md:text-lg text-neutral-700 font-light leading-relaxed">
                        {eixo.diagnostico}
                      </p>

                      <div className="mt-10 space-y-10">
                        {eixo.grupos.map((g, gi) => (
                          <div key={gi} data-testid={`eixo-grupo-${eixo.id}-${gi}`}>
                            <div className="flex items-baseline gap-4 mb-6">
                              <span className="font-serif-display text-3xl md:text-4xl text-[#FFC72C] font-semibold">
                                {String(gi + 1).padStart(2, "0")}
                              </span>
                              <h4 className="font-serif-display text-2xl md:text-3xl text-neutral-900 leading-tight">
                                {g.subtitulo}
                              </h4>
                            </div>
                            <ul className="grid md:grid-cols-2 gap-x-10 gap-y-3 pl-10 md:pl-14">
                              {g.itens.map((it, ii) => (
                                <li
                                  key={ii}
                                  className="flex items-start gap-3 text-sm md:text-base text-neutral-700"
                                >
                                  <span className="mt-[10px] inline-block w-2 h-2 bg-[#003F88] shrink-0" />
                                  <span>{it}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white border-t border-neutral-200 py-20 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <h2 className="font-serif-display text-4xl md:text-5xl leading-tight">
            Continue o diálogo. <em className="italic font-light">Sugira, critique, contribua.</em>
          </h2>
          <p className="mt-6 text-base md:text-lg text-neutral-600 font-light leading-relaxed max-w-2xl mx-auto">
            Este plano foi apresentado em 2024. Hoje, ele segue como base de uma agenda viva — e a
            sua participação é fundamental para o futuro de Lauro de Freitas.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link
              to="/contato"
              data-testid="plano-cta-contato"
              className="inline-flex items-center gap-3 bg-[#003F88] text-white px-7 py-4 text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#002A5C] transition-colors"
            >
              Enviar sua sugestão <Icons.ArrowRight size={16} />
            </Link>
            <Link
              to="/projetos"
              data-testid="plano-cta-projetos"
              className="inline-flex items-center gap-3 border-2 border-[#003F88] text-[#003F88] px-7 py-[14px] text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#003F88] hover:text-white transition-colors"
            >
              Ver projetos aprovados
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
